// =============================================================================
// cgu-diff.ts — Diff par blocs texte pour la comparaison CGU (R15-GOV-05, DBU §2.9 l.2048/1987).
// =============================================================================
// PUR, 0 dépendance. Sert la « Vue côte-à-côte du brouillon en cours d'édition et de la version
// courante publiée, à des fins de relecture des écarts » (DBU §2.9). Deux fonctions :
//   1. `htmlToBlocks` — extrait les BLOCS texte lisibles d'un corps HTML DÉJÀ nettoyé serveur
//      (allowlist @jeko/shared-kernel). On ne rend jamais ce texte comme HTML : il sert au diff
//      ET à un rendu texte accessible. Extraction régex (aucune manipulation du DOM vivant).
//   2. `diffBlocks` — diff LCS classique entre les blocs de la version PUBLIÉE (base) et ceux du
//      BROUILLON (nouveau) → suite d'écarts étiquetés (`unchanged` / `added` / `removed`). Le
//      LIBELLÉ porte l'information (WCAG 1.4.1) ; la couleur, à l'écran, n'est qu'un renfort.

/** Nature d'un bloc dans le diff : inchangé, ajouté au brouillon, retiré depuis le publié. */
export type CguDiffKind = 'unchanged' | 'added' | 'removed';

/** Un bloc du diff : sa nature + le texte lisible du bloc. */
export interface CguDiffBlock {
  readonly kind: CguDiffKind;
  readonly text: string;
}

// Frontières de bloc : fermetures des éléments structurants + sauts de ligne <br>. Chacune devient
// un séparateur de bloc (\n) avant le strip des balises — un titre, un paragraphe, un item de liste
// ou une ligne de table forment un bloc comparable indépendant.
const BLOCK_BOUNDARY = /<\/(?:h[1-6]|p|li|tr|blockquote|div|thead|tbody|table|ul|ol)>|<br\s*\/?>/gi;

// Entités HTML minimales à décoder pour un texte lisible (le contenu est déjà nettoyé serveur).
const ENTITIES: ReadonlyArray<readonly [RegExp, string]> = [
  [/&nbsp;/gi, ' '],
  [/&#0*39;/g, "'"],
  [/&apos;/gi, "'"],
  [/&quot;/gi, '"'],
  [/&lt;/gi, '<'],
  [/&gt;/gi, '>'],
  [/&amp;/gi, '&'],
];

/**
 * Extrait les blocs texte lisibles d'un corps HTML (déjà nettoyé serveur). Insère un séparateur à
 * chaque frontière de bloc, retire les balises restantes, décode les entités usuelles, normalise les
 * espaces et écarte les blocs vides. Renvoie `[]` pour un corps null/vide.
 */
export function htmlToBlocks(html: string | null | undefined): readonly string[] {
  if (html === null || html === undefined || html.length === 0) {
    return [];
  }
  let text = html.replace(BLOCK_BOUNDARY, '\n');
  text = text.replace(/<[^>]*>/g, '');
  for (const [pattern, replacement] of ENTITIES) {
    text = text.replace(pattern, replacement);
  }
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.length > 0);
}

/**
 * Diff LCS entre les blocs de la version PUBLIÉE (`before`, base) et ceux du BROUILLON (`after`,
 * nouveau). Renvoie la suite ordonnée des blocs : `unchanged` (commun), `removed` (présent dans le
 * publié, absent du brouillon), `added` (présent dans le brouillon, absent du publié).
 *
 * `Int32Array` pour la table LCS : son index renvoie toujours `number` (compatible
 * noUncheckedIndexedAccess), sans transtypage ni accès potentiellement `undefined`.
 */
export function diffBlocks(
  before: readonly string[],
  after: readonly string[],
): readonly CguDiffBlock[] {
  const n = before.length;
  const m = after.length;
  const width = m + 1;
  const lcs = new Int32Array((n + 1) * width);

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        before[i] === after[j]
          ? (lcs[(i + 1) * width + (j + 1)] ?? 0) + 1
          : Math.max(lcs[(i + 1) * width + j] ?? 0, lcs[i * width + (j + 1)] ?? 0);
    }
  }

  const out: CguDiffBlock[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      out.push({ kind: 'unchanged', text: before[i] ?? '' });
      i += 1;
      j += 1;
    } else if ((lcs[(i + 1) * width + j] ?? 0) >= (lcs[i * width + (j + 1)] ?? 0)) {
      out.push({ kind: 'removed', text: before[i] ?? '' });
      i += 1;
    } else {
      out.push({ kind: 'added', text: after[j] ?? '' });
      j += 1;
    }
  }
  while (i < n) {
    out.push({ kind: 'removed', text: before[i] ?? '' });
    i += 1;
  }
  while (j < m) {
    out.push({ kind: 'added', text: after[j] ?? '' });
    j += 1;
  }
  return out;
}

/** Vrai si le diff ne contient aucun écart (tous les blocs sont `unchanged`). */
export function isIdenticalDiff(blocks: readonly CguDiffBlock[]): boolean {
  return blocks.every((block) => block.kind === 'unchanged');
}
